import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Table, Button, Space, Input, Modal, Form, InputNumber, Select, Segmented,
  message, Popconfirm, Tag, Typography, Alert, Tooltip
} from 'antd';
import {
  PlusOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  UnorderedListOutlined, TableOutlined
} from '@ant-design/icons';
import useAuth from '../../../../hooks/useAuth';
import offcutApi from './offcutApi';
import OffcutsPivotView from './OffcutsPivotView';
import { PERM, hasPermission, offcutArea, formatThickness, formatArea } from './offcutHelpers';
import './OffcutsPage.css';

const { Title, Text } = Typography;

// Live area preview from the form's current width/height.
function AreaPreview({ form }) {
  Form.useWatch('widthMm', form);
  Form.useWatch('heightMm', form);
  const w = form.getFieldValue('widthMm');
  const h = form.getFieldValue('heightMm');
  const a = offcutArea(w, h);
  if (!a.sqmt) return <Text type="secondary">—</Text>;
  return <Text strong className="tabular-nums">{formatArea(a.sqmt)} m² · {formatArea(a.sqft)} ft²</Text>;
}

export default function OffcutsPage() {
  const { me } = useAuth();
  const canCreate = hasPermission(me, PERM.CREATE);
  const canUpdate = hasPermission(me, PERM.UPDATE);
  const canDelete = hasPermission(me, PERM.DELETE);
  const canPrint = hasPermission(me, PERM.PRINT);

  // View mode — flat List ↔ aggregate Pivot (glass item × piece size).
  const [viewMode, setViewMode] = useState('list');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Glass-item lookup (for the add/edit form).
  const [glassOpts, setGlassOpts] = useState([]);
  const [glassLoading, setGlassLoading] = useState(false);
  const glassById = useMemo(() => {
    const m = new Map(); glassOpts.forEach((g) => m.set(g.itemId, g)); return m;
  }, [glassOpts]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // null = create
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const searchTimer = useRef(null);

  // The flat list only fetches while its view is active — the Pivot view owns
  // its own server-aggregated fetch, so we don't pay for both at once.
  const load = useCallback(async () => {
    if (viewMode !== 'list') return;
    setLoading(true);
    try {
      const res = await offcutApi.list({ page, pageSize, search: search || undefined });
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load off-cuts.');
    } finally { setLoading(false); }
  }, [viewMode, page, pageSize, search]);

  useEffect(() => { load(); }, [load]);

  // Shared filter surface handed to the Pivot view — same search semantics as
  // the list query so the two views always agree on which off-cuts are in scope.
  const pivotFilters = useMemo(() => ({
    search: (search || '').trim() || undefined
  }), [search]);

  const loadGlass = useCallback(async (q) => {
    setGlassLoading(true);
    try { setGlassOpts(await offcutApi.lookups({ q: q || undefined, limit: 50 }) || []); }
    catch { /* soft-fail — the Select just shows no options */ }
    finally { setGlassLoading(false); }
  }, []);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ qty: 1 });
    setModalOpen(true);
    loadGlass('');
  }, [form, loadGlass]);

  const openEdit = useCallback((row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      itemId: row.itemId, thicknessMm: row.thicknessMm, widthMm: row.widthMm, heightMm: row.heightMm,
      warehouseName: row.warehouseName, rackName: row.rackName, refRate: row.refRate,
      refValue: row.refValue, remarks: row.remarks
    });
    setGlassOpts(row.itemId ? [{ itemId: row.itemId, itemName: row.itemName, thicknessMm: row.thicknessMm }] : []);
    setModalOpen(true);
  }, [form]);

  const onGlassPick = useCallback((itemId) => {
    const g = glassById.get(itemId);
    if (g && g.thicknessMm != null && !form.getFieldValue('thicknessMm')) {
      form.setFieldsValue({ thicknessMm: g.thicknessMm });
    }
  }, [glassById, form]);

  const submit = useCallback(async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editing) {
        await offcutApi.update(editing.id, v);
        message.success('Off-cut updated.');
      } else {
        const out = await offcutApi.create(v);
        message.success(`Added ${out.count} off-cut${out.count === 1 ? '' : 's'}.`);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Save failed.');
    } finally { setSaving(false); }
  }, [form, editing, load]);

  const doDelete = useCallback(async (row) => {
    try {
      await offcutApi.remove(row.id);
      message.success(`Removed ${row.offcutCode}.`);
      await load();
    } catch (e) { message.error(e?.response?.data?.error?.message || 'Delete failed.'); }
  }, [load]);

  const printLabel = useCallback(async (row) => {
    try {
      const html = await offcutApi.labelHtml(row.id);
      // Blob URL (no document.write) — matches the WorkOrderScanPage sticker print.
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(url, '_blank');
      if (!win) message.warning('Allow pop-ups to print the label.');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { message.error(e?.response?.data?.error?.message || 'Could not open label.'); }
  }, []);

  const onSearch = useCallback((val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'offcutCode', key: 'offcutCode', width: 168,
      render: (v) => <Text strong className="tabular-nums">{v}</Text> },
    { title: 'Glass', dataIndex: 'itemName', key: 'itemName', ellipsis: true,
      render: (v, r) => <span>{v}{r.glassColor ? <Text type="secondary"> · {r.glassColor}</Text> : null}</span> },
    { title: 'Thk', dataIndex: 'thicknessMm', key: 'thicknessMm', width: 64, align: 'right',
      render: (v) => formatThickness(v) },
    { title: 'Size (mm)', key: 'size', width: 150, align: 'right',
      render: (_, r) => <span className="tabular-nums">{r.widthMm} × {r.heightMm}</span> },
    { title: 'Area (m²)', dataIndex: 'sqmt', key: 'sqmt', width: 96, align: 'right',
      render: (v) => <span className="tabular-nums">{formatArea(v)}</span> },
    { title: 'Location', key: 'loc', width: 150, ellipsis: true,
      render: (_, r) => [r.warehouseName, r.rackName].filter(Boolean).join(' · ') || <Text type="secondary">—</Text> },
    { title: 'Value', dataIndex: 'refValue', key: 'refValue', width: 96, align: 'right',
      render: (v) => (v != null ? <span className="tabular-nums">₹{v}</span> : <Text type="secondary">—</Text>) },
    { title: 'Actions', key: 'actions', width: 132, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          {canPrint ? (
            <Tooltip title="Print label"><Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => printLabel(r)} /></Tooltip>
          ) : null}
          {canUpdate ? (
            <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          ) : null}
          {canDelete ? (
            <Popconfirm title="Remove this off-cut?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => doDelete(r)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : null}
        </Space>
      ) }
  ]), [canPrint, canUpdate, canDelete, printLabel, openEdit, doDelete]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Title level={4} style={{ margin: 0 }}>Off-cuts</Title>
          <Text type="secondary">Reusable leftover glass pieces recorded after cutting.</Text>
        </div>
        <Space>
          <Input.Search allowClear placeholder="Search code / glass" style={{ width: 240 }} onChange={(e) => onSearch(e.target.value)} />
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode(v)}
            options={[
              { value: 'list',  icon: <UnorderedListOutlined />, label: 'List' },
              { value: 'pivot', icon: <TableOutlined />,         label: 'Pivot' }
            ]}
          />
          {viewMode === 'list' ? <Button icon={<ReloadOutlined />} onClick={load} /> : null}
          {canCreate ? <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add off-cut</Button> : null}
        </Space>
      </div>

      {viewMode === 'pivot' ? (
        <OffcutsPivotView filters={pivotFilters} />
      ) : (
      <Card size="small" className="offcut-grid" bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          sticky
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            size: 'small',
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showTotal: (t) => `${t} off-cut${t === 1 ? '' : 's'}`
          }}
        />
      </Card>
      )}

      <Modal
        title={editing ? `Edit off-cut · ${editing.offcutCode}` : 'Add off-cut'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={editing ? 'Save' : 'Add'}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          {!editing ? (
            <Alert type="info" showIcon className="mb-3"
              message="Full sheets are issued whole; record each reusable remnant here as its own labelled piece." />
          ) : null}
          <Form.Item name="itemId" label="Glass item" rules={[{ required: true, message: 'Pick a glass item' }]}>
            <Select
              showSearch filterOption={false}
              placeholder="Search the Glass / Item Master"
              loading={glassLoading}
              onSearch={loadGlass}
              onChange={onGlassPick}
              disabled={!!editing}
              options={glassOpts.map((g) => ({
                value: g.itemId,
                label: `${g.itemName}${g.thicknessMm != null ? ` · ${formatThickness(g.thicknessMm)}mm` : ''}${g.glassColor ? ` · ${g.glassColor}` : ''}`
              }))}
            />
          </Form.Item>
          <div className="grid grid-cols-3 gap-3">
            <Form.Item name="thicknessMm" label="Thickness (mm)" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber min={0.1} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="widthMm" label="Width (mm)" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="heightMm" label="Height (mm)" rules={[{ required: true, message: 'Required' }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div className="flex items-center justify-between mb-2">
            <Text type="secondary">Area</Text>
            <AreaPreview form={form} />
          </div>
          {!editing ? (
            <Form.Item name="qty" label="Number of pieces" tooltip="Each piece gets its own code + label" rules={[{ required: true }]}>
              <InputNumber min={1} max={500} precision={0} step={1} style={{ width: 140 }} />
            </Form.Item>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="warehouseName" label="Warehouse (optional)">
              <Input maxLength={160} placeholder="e.g. Main store" />
            </Form.Item>
            <Form.Item name="rackName" label="Rack (optional)">
              <Input maxLength={120} placeholder="e.g. B3" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="refRate" label="Rate (optional)" tooltip="Report-only — not posted to accounts">
              <InputNumber min={0} style={{ width: '100%' }} prefix="₹" />
            </Form.Item>
            <Form.Item name="refValue" label="Value (optional)" tooltip="Report-only — not posted to accounts">
              <InputNumber min={0} style={{ width: '100%' }} prefix="₹" />
            </Form.Item>
          </div>
          <Form.Item name="remarks" label="Remarks (optional)">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
